import { Injectable } from "@angular/core";
import { Platform } from "@ionic/angular";
import { BehaviorSubject, fromEvent } from "rxjs";
 
import { API_LIB } from "src/library/api.lib";
import { AppstorageTsService } from "../appstorage/appstorage.ts.service";
import { CasinoGameModel } from "src/app/models/casino-game.model";
import { ConfigService } from "../config/config.service";
import { CustomerModel } from "src/app/models/customer-model";
import { delay, first, take } from "rxjs/operators";
import { EventsService } from "../event/event.service";
import { GameHub } from "./game-hub";
import { GeoResponse } from "src/app/interfaces/geo-interface";
import { IabControllerService } from "../iabController/iab-controller.service";
import { isUndefined } from "../codeUtils";
import { JurisdictionService } from "../geo/jurisdiction.service";
import { LogoutService } from "../logout/logout.service";
import { Navigator } from "src/app/services/routing/navigator";
import { RoutingService } from "../routing/routing.service";
import { UserSessionService } from "../user-session/user-session.service";
import { WebservicesService } from "../webservices/webservices.service";
import { GeoLocationStrategyService } from "../geo-location-strategy/geo-location-strategy.service";
import { GeoLocationControllerService } from "../geo-location/geo-location-controller.service";
import { ROUTE_PATHS } from 'src/library/app-routes.lib';

@Injectable({
    providedIn: 'root'
})
export class GameHubControllerService {

  private _instances: Array<any> = [];
  private focusedInstance: BehaviorSubject<any>;
  private gameLaunched: boolean = false;
  private gameLaunchingStrategies: any = {};
  private isMobile: boolean = false
  private launchStrategyType: string = "";
  private LIMITS: any = {}
  public _restrictedGameData: any;
  public launchedGame: any = null;
  public shouldNotifyCustomerRotate: boolean = false;
  private bonusNotificationCount: number = 0;
  private bonusNotificationDisplayedTimesMAX = 3;
  private popupResolve: ((value: boolean) => void) | null = null;

  constructor(
    private appStorage: AppstorageTsService,
    private config: ConfigService,
    private events: EventsService,
    private eventsService: EventsService,
    private gameHub: GameHub,
    private iabController: IabControllerService,
    private jurisdiction: JurisdictionService,
    private logoutService: LogoutService,
    private navigator: Navigator,
    private platform: Platform,
    private routing: RoutingService,
    private geoLocationControllerService: GeoLocationControllerService,
    private geoLocationStrategyService: GeoLocationStrategyService,
    private userSessionService: UserSessionService,
    //private gameTile: GameTileService,
    private webservices: WebservicesService,
  
  ) {
    this.isMobile = this.platform.is('mobile');
    const useInAppBrowser = this.config.isFeatureON(this.config.getConfig(), "FEATURE_USE_IN_APP_BROWSER", true);
    const inAppOrTab = useInAppBrowser ? this.launchMobileApps : this.launchInGameTab;
    this.gameLaunchingStrategies = {
      default: this.launchInGameTab,
      downloadable: this.launchInGameTab,
      mobileWebIos: this.launchInGameTab,
      mobileWebAndroid: this.launchInGameTab,
      mobile: inAppOrTab,
      mobileIos: inAppOrTab,
    };

    fromEvent(window, "message").subscribe((event: any) => {
      this.handleGameEvent(event);
    });

    this.focusedInstance = new BehaviorSubject(null);

    let maxCasinoGames = this.config.getConfig().WEB_LIMIT_CASINOGAME || 1;

    if (this.platform.is('electron')) {
      maxCasinoGames = this.config.getConfig().DOWNLOADABLE_LIMIT_CASINOGAME;
    }

    this.LIMITS = {
      maxCasinoGames: maxCasinoGames
    }

    this.listenForGameRestartEvent();
  }

  listenForGameRestartEvent() {
    this.eventsService.subscribe("event:game:restart", () => {
      setTimeout(() => this.launchCasinoGame(this.launchedGame), 1000);
    });
  }

  private popupData: any = {
    title: "Live dealer bonus",
    message: "Using bonus money on live dealer games is restricted. Please note that players cannot currently use bonus funds on any live dealer games.",
    buttonText: "Yes, I understand",
  }

  protected openStandardConfirmPopover() {
    return this.navigator.openPopover(
      "PopoverStandardConfirmComponent",
      { message: this.popupData }, "standard-popover livedealer",
      false, true, true, -1, 'cover', true
    );
  }

  liveDealerBonusPopover():Promise<boolean> {
    const bonusMoneyUseEnabled = this.config.getConfig().EVOLUTION_BONUS_USE_ENABLED ?? '0';
    if (bonusMoneyUseEnabled === '1' && this.bonusNotificationCount <= Number(this.bonusNotificationDisplayedTimesMAX)) {
      this.bonusNotificationCount++;
      return new Promise((resolve) => {
      this.popupResolve = resolve;
      this.popupData.confirm = this.liveDealerPopupConfirmCallback;
      this.popupData.decline = this.liveDealerPopupDeclineCallback;
      this.openStandardConfirmPopover();
    }); 
    }
    return Promise.resolve(true);
  }

  public liveDealerPopupConfirmCallback = (): any => {
    this.navigator.dismissAll();
    if (this.popupResolve) {
      this.popupResolve(true);
      this.popupResolve = null;
    }
  }

  public liveDealerPopupDeclineCallback = (): any => {
    //TO DO for Waiting Game Data Check from Logged out to Logged In Scenario
    //const gameData = this.gameTile?.waitingGameData;
    //if (!!gameData) {
    //this.gameTile.waitingGameData = null;
    //}
    this.incrementCounter(); 
    if (this.popupResolve) {
      this.popupResolve(false);
      this.popupResolve = null;
    }
  }

  public incrementCounter() {
    this.bonusNotificationCount = 0;
    this.bonusNotificationDisplayedTimesMAX = this.config.getConfig().EVOLUTION_BONUS_USE_MAX ?? 3;
  }

  async launchCasinoGame(data: any) {
    if (data.super_type === 'LIVE_DEALER') {
      const confirmed = await this.liveDealerBonusPopover();
      if (!confirmed) {
        console.log('User declined live dealer popup.');
        this.routing.toLiveDealerTab(true);
        return;
      }
    }
    if (this.gameLaunched) return true;
    this.gameLaunched = true;
    setTimeout(() => (this.gameLaunched = false), 800);

    this.launchedGame = data;
    this.userSessionService
      .getCustomer()
      .pipe(take(1))
      .subscribe((customer: CustomerModel) => {
        let isMobileApp = ((this.getDeviceType() == "mobile" || this.getDeviceType() == "mobileIos") ? true : false);
        let model = new CasinoGameModel(
          data,
          this.config,
          customer,
          this.userSessionService,
          isMobileApp
        );

        this.killAllInstances();
        //this.launchGame(model); //v remove
        if (this.geoLocationStrategyService.isGeoOptimizationEnabled()) {
          this.geoLocationControllerService.executeGeoLocationProcess()
            .then((geoResp: GeoResponse) => {
              if (geoResp.is_allowed) {
                this.events.publish("event:gameLaunched", {
                  customer_id: customer.customerId,
                  game_name: model.game_name,
                  is_demo: false,
                  game_id: data.id,
                  game_order: data.game_order,
                  keyword_name: data.keyword_name,
                  gameUrl: data.gameUrl,
                });
                model.is_demo = false;
                this.launchGame(model);
              }
            })
            .catch((err) => {
              console.log("launch aborted.", err);
            });
        } else {
        this.jurisdiction
          .debounceVerifyJurisdiction(() => this.launchCasinoGame(data))
          .then((geoResp: GeoResponse) => {
            this.events.publish("event:gameLaunched", {
              customer_id: customer.customerId,
              game_name: model.game_name,
              is_demo: false,
              game_id: data.id,
              game_order: data.game_order,
              keyword_name: data.keyword_name,
              gameUrl: data.gameUrl,
            });
            model.is_demo = false;
            this.checkScreenMode(data);
            this.launchGame(model);
          })
          .catch((err) => {
            console.log("launch aborted.", err);
          });
        }
      });
  }

  checkScreenMode(data) {
    this.shouldNotifyCustomerRotate = false;
    if (this.platform.is("mobile") && this.platform.isPortrait()) {
      if (data && data.extra_launch_data) {
        this.appStorage.getSkipAskCustomerRotate().then((userSkipped) => {
          if (!(userSkipped && userSkipped.value == "true")) {
            const launchData = JSON.parse(data.extra_launch_data);
            if (("landscapeOnly" in launchData) && launchData.landscapeOnly) {
              this.shouldNotifyCustomerRotate = true;
            }
          }
        })
      }
    }
  }

  async launchGame(model) {
    this.restrictedGameData = model;
    if (!model.is_demo) {
      this.events.publish("event:balanceState", { show: false, code: "IN_GAME" });
    } else {
      this.events.publish("event:balanceState", { show: false, code: "IN_DEMO" });
    }
    //isMobile = this.platform.is('mobile');  
    //if (this.isMobile) setTimeout(() => this.eventsService.publish("event:bottommenu:hide"), 100);
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent);
    const mobileweb = mobile && !(this.platform.is('capacitor') || this.platform.is('cordova'));
    if (mobileweb) setTimeout(() => this.eventsService.publish("event:bottommenu:hide"), 100);

    const deviceType = this.getDeviceType();
    let launchType = deviceType;
    if (this.platform.is("capacitor")) {
      const isGameNotAllowed = await this.isGameNotAllowedIAB(model);
      console.log("isGameNotAllowed ", isGameNotAllowed)
      if (isGameNotAllowed) {
        launchType = "default";
      }
    }
    if (launchType in this.gameLaunchingStrategies) {
      const launcherFn = this.gameLaunchingStrategies[launchType];
      launcherFn.apply(this, [model]);
    }
  }

  private instanceExist(model) {
    return this.gameHub.instanceExists(model) != -1
  }

  private canLaunchCasinoGame(model) {
    return (this.LIMITS.maxCasinoGames > this.gameHub.totalCasinoGames() || this.instanceExist(model));
  }

  launchCasinoGameDemo(data: any) {
    let isMobileApp = this.getDeviceType() == "mobile" ? true : false
    let model = new CasinoGameModel(data, this.config, null, this.userSessionService, isMobileApp);
    if (this.canLaunchCasinoGame(model)) {
      this.launchGame(model);
    } else {
      //this.showReachedLimitDialog('casino games', this.LIMITS.maxCasinoGames);
      this.killAllInstances();
      setTimeout(() => {
        this.launchGame(model);
      }, 500);
    }
  }

  private async isGameNotAllowedIAB(model: { id?: string | number, vendor_ident?: string }): Promise<boolean> {
    const vendorName = model?.vendor_ident || await this.iabController.getVendor(model?.id);
    console.log("vendorName", vendorName)
    const device = this.platform.is("android") ? "android" : this.platform.is("ipad") ? "ipad" : "iphone";
    const gameInputParts = [vendorName, model?.id, device].filter(Boolean);
    const gameInput = gameInputParts ? gameInputParts.join("_").toUpperCase() : "";
    let flagList: string[] = ["internal"]
    try {
      const featureConfig = this.config.getConfig().FEATURE_MOBILE_IFRAME_VENDORS;
      if (featureConfig) {
        flagList = JSON.parse(featureConfig);
      }
    } catch (e) {
      // if there is an error will open from iframe
      return true;
    }
    const isGameNotAllowed: boolean = flagList.some(flag => {
      const flagParts = flag.toUpperCase().split('_');
      return flagParts.every(part => gameInput.includes(part));
    });
    return isGameNotAllowed;
  }

  getDeviceType() {
    let deviceType = "default";
    if (this.platform.is("electron")) {
      deviceType = "downloadable";
    } else if (
      this.platform.is("mobileweb") &&
      !this.platform.is("capacitor") &&
      this.platform.is("ios")
    ) {
      deviceType = "mobileWebIos";
    } else if (
      this.platform.is("mobileweb") &&
      !this.platform.is("capacitor") &&
      this.platform.is("android")
    ) {
      deviceType = "mobileWebAndroid";
    } else if (this.platform.is("capacitor") && this.platform.is("ios")) {
      deviceType = "mobileIos";
    } else if (this.platform.is("capacitor")) {
      deviceType = "mobile";
    }
    console.log("Device type:", deviceType);
    return deviceType;
  }

  focusInstance(instance_id) {
    this.gameHub.focusInstance(instance_id);

    const instance = this.gameHub.getInstance(instance_id);

    if (instance.viewContainer == "IFRAME") {
      this.routing.toGameTab();
      this.focusedInstance.next(instance_id);
    }
    this.gameHub.unHighlightInstance(instance_id);
    this.events.publish("event:mygames:unhighlight");
    this.gameHub.muteGames(instance_id);
  }

  unfocusAllInstances() {
    let instance = null;
    let instances = this.getAllInstances();
    if (Object.keys(instances).length) {
      for (var id in instances) {
        instance = this.getInstance(id);
        instance.isActive = false;
        this._instances[id] = instance;
      }
    }
  }

  handleGameEvent(event) {
    if (!isUndefined(event.data)) {
      this.handleSpendLimitError(event);
      if (!isUndefined(event.data.name)) {
      // windowID maps to our gameHub instanceID
      let instance_id = event.data.windowID;
      let instance = this.gameHub.getInstance(instance_id);
      switch (event.data.name) {
        case "registerActivity":
          this.events.publish("event:inactivity:reset");
          break;
        case "close_game":
            this.closeGame(event);
          break;
        case "show_buy_chips":
          break;
        case "action_request":
        case "bet_request":
        case "insurance_request":
        case "post_ante_request":
        case "post_blind_request":
        case "player_timeout":
          let focusedInstanceId = this.gameHub.getFocusedInstance();
          if (instance_id !== focusedInstanceId) {
            this.events.publish("event:mygames:highlight");
            this.gameHub.highlightInstance(instance_id);
          }
          break;
      }
    }
  }
  }

  getInstance(id) {
    return this._instances[id];
  }

  updateInstance(instance_id, instance) {
    this._instances[instance_id] = instance;
  }
  getLaunchStrategyType() {
    return this.launchStrategyType;
  }

  getAllInstances() {
    return this._instances;
  }
  getFocusedInstance() {
    return this.focusedInstance;
  }

  launchInGameTab = (model) => {
    const instanceId = this.gameHub.addCasinoGame(model, {
      viewContainer: "IFRAME",
    });

    this.events.subscribe("event:authentication:logout", () => {
      this.removeInstance(instanceId);
    });

    this.events.subscribe("event:game:close", () => {
      this.removeInstance(instanceId);
    });

    if (model.is_demo) {
      this.focusInstance(instanceId);
    } else {
      const obj: any = {
        customer_id: model.customer.customerId,
        session_token: this.userSessionService.getSessionToken(),
        game_launcher_id: "-1",
      };
      const game_lauch_time_out = this.platform.is('capacitor') ? 10 : 1000;
      this.webservices.post("games/close-game", obj).subscribe(() => {
        setTimeout(() => {
          this.focusInstance(instanceId);
        }, game_lauch_time_out);
      });
    }

    this.setLaunchStrategyType("launchInGameTab");

    //seo stuff:
    const gId = model.id;
    const externalLinksData = this.config.getExternalLinks();
    let link = externalLinksData.payload.filter((link) => link.code == gId);
    link = !!link.length ? link[0] : {};
  };

  async launchMobileApps(model: any) {
     
    const gamePrms: any = {
      gameLaunchId: model.id,
      vendor_name: model.vendor_ident
    };
    if (!model.is_demo && model.userSessionService?.sessionToken) {
      gamePrms.session_token = model.userSessionService.sessionToken;
    }
    if (model.customer?.customerId) {
      gamePrms.customer_id = model.customer.customerId;
    }
     if(model.is_demo) {
      this.iabController.launchIAB(model.url, gamePrms);
    } else {

      const deviceType = this.getDeviceType();
      const delayTime = 0;
      await this.closeAllGamesBeforeLaunch(model);
      this.webservices.getApiNoRedirect(model.url, deviceType, gamePrms)
        .pipe(delay(delayTime), first()).subscribe({
          next: (resp: any) => {
            if (resp?.result && resp.payload) {
              const url = resp.payload.href ?? resp.payload.src ?? '';
              if (url) {
                this.iabController.launchIAB(url, gamePrms);
              }
            }
          },
          error: (error) => {
            console.error("Error occurred:", error?.error_code);
            this.events.publish("event:balanceState", { show: true })
            if (error?.error_code === "PLAYER_HAS_ACTIVE_BONUS_CANNOT_USED_BY_GAME") {
              localStorage.setItem('skipActiveBonusCheck', 'true');
              setTimeout(() => {
                this.navigator.openPopover('RestrictedMoneyPopover', { message: '' }, 'standard-popover levelup', false, true, true, -1, 'cover', true);
              }, 700);
            }
          }
        });
    }
  }

  private closeAllGamesBeforeLaunch(model: any): Promise<boolean> {
    return new Promise((resolve) => {
      if (model?.is_demo) {
        return resolve(true);
      }
      const payload = {
        customer_id: model?.customer?.customerId,
        session_token: this.userSessionService.getSessionToken(),
        game_launcher_id: "-1",
      };

      this.webservices.post("games/close-game", payload).subscribe({
        next: () => resolve(true),
        error: () => resolve(true),
      });
    });
  }

  setLaunchStrategyType(val) {
    this.launchStrategyType = val;
  }

  public removeInstance(instance_id) {
    this.eventsService.publish("game:fullscreen:exit");

    if (!isUndefined(instance_id)) {
      const instance = this.gameHub.getInstance(instance_id);

      if (!isUndefined(instance)) {

        if (instance.model.getInstanceCategoryType() === "casino-game") {
          this.events.publish("event:balanceState", { show: true });
        }

        if (instance.viewContainer == "IFRAME") {
          this.events.publish("event:gameHub:removeGame", instance_id);
        }
        // remove from GameHub
        this.gameHub.removeInstance(instance_id);

        // Load next instance or just go back to Lobby
        const next_instance = this.gameHub.getNextInstance();

        if (next_instance > 0) {
          this.focusInstance(next_instance);
          const next_insatnce_model = this.gameHub.getInstance(next_instance);
          if (instance.viewContainer == "IFRAME") return this.routing.defaultRoute();
          return
        }
      }
    }
  }

  killAllInstances() {
    let instances = this.gameHub.getAllInstances();

    if (Object.keys(instances).length) {
      for (var id in instances) {
        this.killInstance(id);
      }
    }
  }

  killInstance(instance_id) {
    if (!isUndefined(instance_id)) {
      let instance = this.gameHub.getInstance(instance_id);
      if (!isUndefined(instance)) {
        if (instance.viewContainer == 'NEWWINDOW') {
          // in case the window is closed from x on the window
          if (instance.windowInstance) instance.windowInstance.close();
        }
        if (instance.viewContainer == "IFRAME") {
          this.events.publish("event:gameHub:removeGame", instance_id);
          if (instance.model.getInstanceCategoryType() === "casino-game") {
            this.events.publish("event:balanceState", { show: true });
          }
        }
        // remove from GameHub

        this.gameHub.removeInstance(instance_id);
      }
    }
  }

  public launchDailySpin(spins) {
    if (this.geoLocationStrategyService.isGeoOptimizationEnabled()) {
      this.geoLocationControllerService.executeGeoLocationProcess()
        .then((geoResp: GeoResponse) => {
          if (geoResp.is_allowed) {
            return this.continueDailySpin(spins);;
          } else {
            console.log('geo check failed');
          }
        }).catch((err) => {
          console.log("launchDailySpin: launch aborted.", err);
        });
    } else {
    this.jurisdiction
      .verifyJurisdiction(() => this.launchDailySpin(spins))
      .then((geoResp: GeoResponse) => {
        return this.continueDailySpin(spins);
      })
      .catch((err: any) => {
        console.error(err);
      });
    }
  }

  private continueDailySpin(spins: any): any {
    this.navigator.navigateTo("DailySlotComponent", {
      forceModal: true,
      customModalClass: "custom-modal-class-transparent-full-height"
    },
      { availableSpins: spins });
  }

  public getLobbyTags() {
    return this.webservices.get(API_LIB.LOBBY_TAGS);
  }

  public get restrictedGameData(): any {
    return this._restrictedGameData;
  }

  public set restrictedGameData(data: any) {
    this._restrictedGameData = data;
  }

  private handleSpendLimitError(event) {
    //NYX vendor sends a message when the spend limit is exceeded
    // data: {gcmevent: "redirect"}
    if (event?.data?.gcmevent === "redirect") {
      this.closeGame(event);
}
  }

  private closeGame(event) {
    this.removeInstance(event.data.windowID);
    this.eventsService.publish('event:tracker:gameClosed');
    if (
      !isUndefined(event.data.closeCode) &&
      event.data.closeCode == "CLT_LOGOUT"
    ) {
      this.logoutService.onLogout(
        { code: "INACTIVITY", reason: "GAME_CLT_LOGOUT" }
      );
    } else {
      if (this.navigator.getCurrentActiveLobby() === ROUTE_PATHS.GAME) {
        this.routing.defaultRoute();
      } else {
        this.navigator.navigateUserToLastActivePath();
      }
    }
  }
}
